import * as React from 'react'

import { DropdownMenu } from 'components/ds'

export type DropdownMenuSection = {
  /** Stable key for the group; not rendered. */
  id: string
  /** Heading shown above the group, when headings are warranted. */
  label?: string
  /** Menu items; entries gated off by permissions may be `null`. */
  items: React.ReactNode[]
}

type Props = {
  sections: DropdownMenuSection[]
  /**
   * Renders every surviving item as one flat list, with no separators or
   * headings at all — for menus condensed enough (e.g. aggregated search
   * results) that group chrome would outweigh the handful of items it groups.
   */
  flat?: boolean
}

/**
 * Renders grouped dropdown menu items, omitting the grouping chrome that only
 * earns its place on a fuller menu.
 *
 * Empty groups are dropped, and separators are emitted only *between* the
 * surviving groups. Headings are shown only when at least two labelled groups
 * survive, since a heading exists to tell its group apart from a labelled
 * sibling.
 *
 * Both rules matter on permission-limited menus: a playlist inside a channel
 * shared with us collapses to a single "Actions" group, which would otherwise
 * open with a rule floating above its first item and a heading labelling the
 * whole menu.
 */
const DropdownMenuSections = ({ sections, flat }: Props): React.ReactNode => {
  const visibleSections = sections
    .map(section => ({ ...section, items: section.items.filter(Boolean) }))
    .filter(section => section.items.length > 0)
  const showLabels =
    !flat && visibleSections.filter(section => section.label).length > 1

  return (
    <>
      {visibleSections.map(({ id, label, items }, index) => (
        <React.Fragment key={id}>
          {!flat && index > 0 && <DropdownMenu.Separator />}
          {showLabels && label && (
            <DropdownMenu.Label>{label}</DropdownMenu.Label>
          )}
          {items.map((item, itemIndex) => (
            <React.Fragment key={itemIndex}>{item}</React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  )
}

export default DropdownMenuSections
